import * as React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { BaggageType, OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { useTheme, ThemeProvider } from '../../../theme';
import PetPopup from '../PetPopup/PetPopup';
import Modal from '../../../Modal';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import { getAnimalServices, getSegments, getTravellers } from '../../../Checkout/store/order/selectors';
import { useAnimalService } from '../hooks';
import { useRenderers } from '../../../renderProps';
import { WxEvents } from '../../../DataLayerAnalytics/types';
import BaggageSummary from '../../../ServiceSummary/services/BaggageSummary';
import { useCart } from '../../../Checkout/components/Checkout/Stepbar/hooks';
import { useIsCanModifyServices } from '../../hooks';
import { useToggleable } from '../../../hooks';
import Close from '../Close/Close';
import WebskyAnalyticsApollo from '../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { getIsLoadingServiceByParams } from '../../../Checkout/store/loadingServicesParams/selectors';
import { useGdsServiceEventToggleable } from '../../../Checkout/components/Checkout/hooks';

interface Props {
	aggregationIcons?: React.ReactNode[];
	aggregationMode?: boolean;
	segmentId?: string;
}

const Animal: React.FC<Props> = ({ aggregationIcons, aggregationMode, segmentId }) => {
	const { Animal: css } = useTheme('CheckoutAdditionalService');
	const { t, i18n } = useTranslation('Checkout');

	const segments = useSelector(getSegments);
	const services = useSelector(getAnimalServices);
	const travellers = useSelector(getTravellers);
	const isLoadingByParams = useSelector(getIsLoadingServiceByParams(services[0]));

	const { isOpen, open, close } = useToggleable(false);
	const { servicesByRfisc, minPrice, totalPrice, onSave, onClear } = useAnimalService(services, travellers);

	useGdsServiceEventToggleable({
		services,
		serviceTypes: [OrderAdditionalServiceGdsServiceServiceType.Baggage],
		serviceSubTypes: [BaggageType.PetInCabin, BaggageType.PetInHold, BaggageType.PetInHandCarrier],
		onOpen: open
	});

	const { onDeleteItem } = useCart();

	useEffect(() => {
		if (!isLoadingByParams) {
			close();
		}
	}, [isLoadingByParams]);

	const isCanModifyServices = useIsCanModifyServices(services);

	React.useEffect(() => {
		if (isOpen) {
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.PetsList);
		}
	}, [isOpen]);

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	return (
		<>
			<BaggageSummary
				segmentId={segmentId}
				onServiceDelete={onDeleteItem}
				isAnimalBaggage
			>
				{content => (
					<ServiceWrapper
						className={css.container}
						header={t('Animal')}
						description={
							i18n.exists('Checkout:Take your pet in a trip') ? t('Checkout:Take your pet in a trip') : ''
						}
						priceFrom={minPrice}
						priceTotal={totalPrice}
						buttons={[
							{
								text: isCanModifyServices ? t('Change') : t('More details'),
								action: open,
								buttonType: 'change'
							}
						]}
						isSelected={!!content}
						isLoading={isLoadingByParams}
						addClick={open}
						addButtonText={!isCanModifyServices ? t('More details') : null}
						aggregationIcons={aggregationIcons}
						mode={aggregationMode ? 'aggregator' : 'default'}
						coverClassName={css.animal_aggregator}
					>
						<ThemeProvider
							value={{
								ServiceSummary: {
									ServiceSummaryOption: {
										option_icon: css.option_icon,
										option__icon: css.option__icon,
										option__name: css.option__name
									}
								}
							}}
						>
							{content}
						</ThemeProvider>
					</ServiceWrapper>
				)}
			</BaggageSummary>

			<Modal
				maxWidth="sm"
				classes={{ root: css.modal__root }}
				open={isOpen}
				onClose={close}
				closeButtonRenderer={onClick => <Close onClick={onClick} />}
			>
				<PetPopup
					segments={segments}
					services={servicesByRfisc}
					travellers={travellers}
					isLoading={isLoadingByParams}
					onConfirm={onSave}
					onClear={onClear}
					onClose={close}
					readOnly={!isCanModifyServices}
					aggregationMode={aggregationMode}
				/>
			</Modal>
		</>
	);
};

export default Animal;
